/**
 * NOTIFICATION HOOKS
 * 
 * React hooks for managing notifications in leBusiness
 */

import { useState, useEffect } from 'react';
import { NotificationService, type Notification, type NotificationPreferences } from '@/lib/services/notificationService';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Hook to get all notifications
 */
export function useNotifications(limit = 50) {
  return useQuery({
    queryKey: ['notifications', limit],
    queryFn: async () => {
      try {
        const { data, error } = await NotificationService.getAll(limit);
        if (error) throw error;
        return data;
      } catch (error) {
        // Return empty array if Supabase not set up yet
        console.warn('Notifications not available yet:', error);
        return [];
      }
    },
  });
}

/**
 * Hook to get unread notification count
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      try {
        const { count, error } = await NotificationService.getUnreadCount();
        if (error) throw error;
        return count;
      } catch (error) {
        // Return 0 if Supabase not set up yet
        console.warn('Unread count not available yet:', error);
        return 0;
      }
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}

/**
 * Hook to mark notification as read
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await NotificationService.markAsRead(notificationId);
      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Hook to mark all notifications as read
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      const { error } = await NotificationService.markAllAsRead();
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Hook to delete notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await NotificationService.delete(notificationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Hook to get notification preferences
 */
export function useNotificationPreferences() {
  return useQuery({
    queryKey: ['notification-preferences'],
    queryFn: async () => {
      try {
        const { data, error } = await NotificationService.getPreferences();
        if (error) throw error;
        return data;
      } catch (error) {
        // Return default preferences if Supabase not set up yet
        console.warn('Notification preferences not available yet:', error);
        return {
          enabled: true,
          email_enabled: true,
          sms_enabled: false,
          whatsapp_enabled: false,
          compliance_enabled: true,
          compliance_email: true,
          compliance_sms: false,
          invoice_enabled: true,
          invoice_email: false,
          payment_enabled: true,
          payment_email: true,
          stock_enabled: true,
          stock_email: false,
          treasury_enabled: true,
          treasury_email: true,
          hr_enabled: true,
          hr_email: false,
          system_enabled: true,
          system_email: false,
          weekend_notifications: false,
          compliance_days_before: [7, 3, 1],
          compliance_send_daily_summary: true,
          compliance_summary_time: '09:00',
        };
      }
    },
  });
}

/**
 * Hook to update notification preferences
 */
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (preferences: Partial<NotificationPreferences>) => {
      const { data, error } = await NotificationService.updatePreferences(preferences);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });
}

/**
 * Hook for real-time notifications
 */
export function useRealtimeNotifications(userId: string) {
  const queryClient = useQueryClient();
  const [latestNotification, setLatestNotification] = useState<Notification | null>(null);
  
  useEffect(() => {
    if (!userId) return;
    
    const { unsubscribe } = NotificationService.subscribeToNotifications(
      userId,
      (notification) => {
        setLatestNotification(notification);
        
        // Invalidate queries to update UI
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        
        // Show browser notification if supported
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(notification.title, {
            body: notification.message,
            icon: '/logo.png',
            tag: notification.id,
          });
        }
        
        // Play notification sound (optional)
        const audio = new Audio('/notification-sound.mp3');
        audio.play().catch(() => {
          // Ignore errors (user might have disabled autoplay)
        });
      }
    );
    
    return () => {
      unsubscribe();
    };
  }, [userId, queryClient]);
  
  return { latestNotification };
}

/**
 * Hook to request browser notification permission
 */
export function useNotificationPermission() {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  
  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);
  
  const requestPermission = async () => {
    if ('Notification' in window) {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    }
    return 'denied';
  };
  
  return { permission, requestPermission };
}

/**
 * Hook to create a notification (for testing/manual creation)
 */
export function useCreateNotification() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (notification: Partial<Notification>) => {
      const { data, error } = await NotificationService.create(notification);
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
